// D:\directiveai-react\frontend\src\components\AGTUps\products\AGTCashFlow-products\Design-DuesPayments\PaymentsSetupWizard.js

import React, { useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Tooltip,
  LinearProgress,
  Fade,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { styled, darken } from '@mui/material/styles';
import { DollarSign, Calendar, Repeat as RepeatIcon, Receipt } from 'lucide-react';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

// Color base para el botón
const blue = '#007BFE';

// ----- ESTILOS PERSONALIZADOS -----

// Dialog sin sombra y con bordes redondeados
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: '28px',
      padding: theme.spacing(2),
      overflow: 'hidden',
      boxShadow: 'none',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
    }
  };
});

// Contenido de cada paso con animación de transición
const StepContent = styled(DialogContent)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  transition: 'all 0.3s ease-in-out',
}));

// Indicador de pasos
const StepIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1)
}));

// Punto para cada paso (con estado activo y completado)
const StepDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'completed'
})(({ theme, active, completed }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: active
    ? (theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main)
    : completed
      ? (theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.6)' : theme.palette.primary.light)
      : (theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : theme.palette.grey[300]),
  transition: 'all 0.3s ease-in-out',
  transform: active ? 'scale(1.2)' : 'scale(1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.1)'
  }
}));

// Tooltip sin sombra
const NoShadowTooltip = styled(Tooltip)(() => ({
  '& .MuiTooltip-tooltip': {
    boxShadow: 'none'
  }
}));

// ----- FUNCIONES AUXILIARES -----

// Calcula las próximas fechas de pago basándose en la fecha de vencimiento inicial, la frecuencia y el número de fechas a mostrar
const calculateNextPaymentDates = (dueDate, frequency, count = 3) => {
  const dates = [];
  if (!dueDate) return dates;
  let currentDate = new Date(dueDate);
  for (let i = 0; i < count; i++) {
    // Clonar la fecha para agregarla al array
    dates.push(new Date(currentDate));
    if (frequency === 'monthly') {
      currentDate.setMonth(currentDate.getMonth() + 1);
    } else if (frequency === 'yearly') {
      currentDate.setFullYear(currentDate.getFullYear() + 1);
    }
  }
  return dates;
};

// ----- COMPONENTE SUMMARY -----

const SummaryContent = ({ steps, paymentData }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'text.primary'
        }}
      >
        Payment Summary
      </Typography>
      <Grid container spacing={2}>
        {steps.map((step, index) => (
          <Grid item xs={12} key={index}>
            <Paper
              sx={{
                p: 2,
                borderRadius: '16px',
                boxShadow: 'none',
                backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(128, 128, 128, 0.25)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
                backdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                WebkitBackdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                border: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? '1px solid rgba(128, 128, 128, 0.3)'
                  : 'none'
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main
                }}
              >
                {step.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.8)'
                    : 'text.secondary'
                }}
              >
                {step.getValue ? step.getValue(paymentData) : 'Not provided'}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ----- COMPONENTE PAYMENTSSETUPWIZARD -----

/**
 * Props:
 * - open: boolean para mostrar/ocultar el diálogo
 * - onClose: función para cerrar el diálogo
 * - initialData: objeto con datos iniciales (para edición o creación)
 * - onSubmit: función asíncrona que se llamará al finalizar el wizard
 * - mode: "create" | "edit" para identificar el flujo
 */
const PaymentsSetupWizard = ({ open, onClose, initialData = {}, onSubmit, mode = 'create' }) => {
  // Estado local para almacenar los datos del pago
  const [paymentData, setPaymentData] = useState({
    title: '',
    amount: '',
    dueDate: '',
    paymentType: 'recurring', // 'recurring' o 'once'
    frequency: 'monthly',
    category: 'software',
    payee: '',
    description: '',
    reminders: true,
    automaticPayment: false,
    ...initialData
  });
  
  // Estados para el wizard
  const [activeStep, setActiveStep] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reinicia el wizard al cerrar
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setShowSummary(false);
      setPaymentData({
        title: '',
        amount: '',
        dueDate: '',
        paymentType: 'recurring',
        frequency: 'monthly',
        category: 'software',
        payee: '',
        description: '',
        reminders: true,
        automaticPayment: false,
        ...initialData
      });
    }
  }, [open, initialData]);

  // Manejo de cambios en los campos
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setPaymentData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  // Definición de los pasos base (sin el paso de resumen)
  const baseSteps = useMemo(() => [
    {
      title: 'Basic Information',
      description: 'Enter the payment title, amount and due date.',
      required: true,
      field: 'title',
      getValue: (data) => data.title || 'Not provided',
      component: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Payment Title"
              name="title"
              value={paymentData.title}
              onChange={handleChange}
              required
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount"
              name="amount"
              type="number"
              value={paymentData.amount}
              onChange={handleChange}
              InputProps={{
                startAdornment: <DollarSign size={16} style={{ marginRight: 8, color: 'inherit' }} />
              }}
              required
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Due Date"
              name="dueDate"
              type="date"
              value={paymentData.dueDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Grid>
        </Grid>
      )
    },
    {
      title: 'Additional Details',
      description: 'Set up payment type, frequency and more.',
      required: true,
      field: 'paymentType',
      getValue: (data) =>
        `Type: ${data.paymentType}${data.paymentType === 'recurring' ? `, Frequency: ${data.frequency}` : ''}, Category: ${data.category}`,
      component: (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required sx={{ mt: 2 }}>
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                Payment Type
              </InputLabel>
              <Select
                label="Payment Type"
                name="paymentType"
                value={paymentData.paymentType}
                onChange={handleChange}
                sx={{
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  },
                  '& .MuiSvgIcon-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                <MenuItem value="recurring">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RepeatIcon size={16} />
                    Recurring
                  </Box>
                </MenuItem>
                <MenuItem value="once">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Calendar size={16} />
                    One-time
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {paymentData.paymentType === 'recurring' && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required sx={{ mt: 2 }}>
                  <InputLabel
                    sx={{
                      color: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.7)'
                        : undefined,
                      '&.Mui-focused': {
                        color: (theme) => theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : undefined
                      }
                    }}
                  >
                    Frequency
                  </InputLabel>
                  <Select
                    label="Frequency"
                    name="frequency"
                    value={paymentData.frequency}
                    onChange={handleChange}
                    sx={{
                      borderRadius: '28px',
                      color: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : undefined
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : undefined
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.3)'
                          : undefined
                      },
                      '& .MuiSvgIcon-root': {
                        color: (theme) => theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : undefined
                      }
                    }}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {paymentData.dueDate && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(128, 128, 128, 0.25)'
                        : 'background.default',
                      backdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                      WebkitBackdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                      border: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '1px solid rgba(128, 128, 128, 0.3)'
                        : 'none',
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'text.primary'
                      }}
                    >
                      Next Payment Dates:
                    </Typography>
                    {calculateNextPaymentDates(paymentData.dueDate, paymentData.frequency, 3).map((date, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          color: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(255, 255, 255, 0.8)'
                            : 'text.secondary'
                        }}
                      >
                        {new Date(date).toLocaleDateString()}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required sx={{ mt: 2 }}>
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                Category
              </InputLabel>
              <Select
                label="Category"
                name="category"
                value={paymentData.category}
                onChange={handleChange}
                sx={{
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  },
                  '& .MuiSvgIcon-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                <MenuItem value="software">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt size={16} style={{ color: '#4f46e5' }} />
                    Software & Services
                  </Box>
                </MenuItem>
                <MenuItem value="rent">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt size={16} style={{ color: '#0891b2' }} />
                    Rent & Lease
                  </Box>
                </MenuItem>
                <MenuItem value="utilities">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt size={16} style={{ color: '#059669' }} />
                    Utilities
                  </Box>
                </MenuItem>
                <MenuItem value="supplies">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt size={16} style={{ color: '#7c3aed' }} />
                    Office Supplies
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            {/* Campo de Payee con helperText y tooltip */}
            <NoShadowTooltip title="Enter the name of the person or organization receiving the payment">
              <TextField
                fullWidth
                label="Payee"
                name="payee"
                value={paymentData.payee}
                onChange={handleChange}
                helperText="Who is receiving the payment?"
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '28px',
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : undefined,
                    '&.Mui-focused': {
                      color: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  }
                }}
              />
            </NoShadowTooltip>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              multiline
              rows={3}
              value={paymentData.description}
              onChange={handleChange}
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : undefined
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                mt: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    name="reminders"
                    checked={paymentData.reminders}
                    onChange={handleChange}
                  />
                }
                label="Enable payment reminders"
              />
              <FormControlLabel
                control={
                  <Switch
                    name="automaticPayment"
                    checked={paymentData.automaticPayment}
                    onChange={handleChange}
                  />
                }
                label={
                  <Box>
                    <Typography>Automatic payment</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Payment will be automatically registered in cash flow on due date
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>
        </Grid>
      )
    }
  ], [paymentData, handleChange]);

  // Creamos el arreglo completo de pasos incluyendo el paso de resumen
  const steps = useMemo(() => [
    ...baseSteps,
    {
      title: 'Review Payment',
      description: 'Please review your payment details before submitting.',
      required: false,
      getValue: () => 'Review all entered details',
      component: (
        <Suspense fallback={<Box sx={{ p: 3 }}>Loading summary...</Box>}>
          <SummaryContent steps={baseSteps} paymentData={paymentData} />
        </Suspense>
      )
    }
  ], [baseSteps, paymentData]);

  // Función para validar el paso actual
  const validateStep = useCallback((stepIndex) => {
    // Primer paso: validar título, monto y fecha
    if (stepIndex === 0) {
      return (
        paymentData.title.trim() !== '' &&
        Number(paymentData.amount) > 0 &&
        paymentData.dueDate !== ''
      );
    }
    
    // Segundo paso: validar tipo de pago y, si es recurrente, que tenga frecuencia y categoría
    if (stepIndex === 1) {
      if (paymentData.paymentType === 'recurring') {
        return paymentData.frequency !== '' && paymentData.category !== '';
      }
      return paymentData.category !== '';
    }
    
    // Para el paso de revisión u otros, asumimos que no se requieren validaciones adicionales
    return true;
  }, [paymentData]);

  // Manejo de la transición entre pasos
  const handleStepTransition = useCallback((nextStep) => {
    setTransitioning(true);
    setTimeout(() => {
      setActiveStep(nextStep);
      setTransitioning(false);
    }, 300);
  }, []);

  // Función para avanzar al siguiente paso
  const handleNext = useCallback(async () => {
    if (!validateStep(activeStep)) return;
    if (activeStep === baseSteps.length - 1 && !showSummary) {
      setShowSummary(true);
      return;
    }
    if (showSummary || activeStep === steps.length - 1) {
      setIsSubmitting(true);
      try {
        await onSubmit(paymentData);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    handleStepTransition(activeStep + 1);
  }, [activeStep, baseSteps.length, showSummary, onSubmit, paymentData, validateStep, handleStepTransition, steps.length]);

  // Función para retroceder
  const handleBack = useCallback(() => {
    if (showSummary) {
      setShowSummary(false);
      return;
    }
    handleStepTransition(activeStep - 1);
  }, [activeStep, showSummary, handleStepTransition]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <Box sx={{ width: '100%', mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={((activeStep + (showSummary ? 1 : 0)) / steps.length) * 100}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(0,123,254,0.12)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue
            }
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{ display: 'block', mt: 0.5 }}
        >
          Step {activeStep + 1} of {steps.length}
        </Typography>
      </Box>
      <StepIndicator>
        {steps.map((step, index) => (
          <NoShadowTooltip key={index} title={step.title}>
            <StepDot
              active={index === activeStep}
              completed={index < activeStep}
              onClick={() => {
                if (index < activeStep) handleStepTransition(index);
              }}
            />
          </NoShadowTooltip>
        ))}
      </StepIndicator>
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          {showSummary ? 'Review Payment' : steps[activeStep].title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {showSummary
            ? 'Review your payment details before submitting'
            : steps[activeStep].description}
        </Typography>
      </DialogTitle>
      <StepContent sx={{ opacity: transitioning ? 0 : 1 }}>
        {showSummary ? steps[steps.length - 1].component : steps[activeStep].component}
      </StepContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {(activeStep > 0 || showSummary) && (
          <Button
            onClick={handleBack}
            startIcon={
              <ArrowBack
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue
                }}
              />
            }
            sx={{
              textTransform: 'none',
              borderRadius: '28px',
              color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue,
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          disableElevation
          onClick={handleNext}
          disabled={!validateStep(activeStep) || isSubmitting}
          endIcon={!showSummary && <ArrowForward />}
          sx={{
            textTransform: 'none',
            borderRadius: '30px',
            backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : blue,
            color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#000000' : 'white',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.85)'
                : darken(blue, 0.2),
              boxShadow: 'none'
            }
          }}
        >
          {isSubmitting
            ? mode === 'create'
              ? 'Creating Payment...'
              : 'Saving Changes...'
            : showSummary
              ? mode === 'create'
                ? 'Create Payment'
                : 'Save Changes'
              : activeStep === baseSteps.length - 1
                ? 'Review'
                : 'Continue'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default PaymentsSetupWizard;
